import { ChevronDown, ChevronRight } from 'lucide-react';
import { Fragment, useState, type ReactNode } from 'react';

import { Nut } from './Nut.js';

export interface CotBang<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  align?: 'left' | 'right' | 'center';
  width?: string;
  /** Cột luôn hiện khi bảng cuộn ngang. */
  ghim?: boolean;
}

export interface DataTableProps<T> {
  columns: CotBang<T>[];
  rows: T[];
  keyOf: (row: T) => string;
  /** Dòng tổng ở cuối bảng. */
  tongKet?: Partial<Record<string, ReactNode>>;
  dangTai?: boolean;
  /** Hiện khi không có dòng nào. */
  khiRong?: ReactNode;
  /** Phân trang theo cursor. */
  conNua?: boolean;
  dangTaiThem?: boolean;
  onTaiThem?: () => void;
  caption?: string;
  /**
   * Tổng số dòng có trên máy chủ. Chân bảng ghi `8 / 12` — "đang xem 8 trong
   * 12". Thiếu số này thì người dùng không biết nút "Tải thêm" còn bao nhiêu.
   */
  tongSo?: number;
  /**
   * Nội dung **dòng mở rộng** của một hàng. Khai hàm này là bảng tự thêm cột
   * nút ▸ ở đầu và tự giữ trạng thái mở / đóng.
   *
   * Có ở đây chứ không để từng trang tự dựng `<table>`: trang Đối soát từng
   * làm vậy, và dòng chi tiết của nó lệch padding, lệch cỡ chữ và lệch màu
   * viền so với mọi bảng khác trong app.
   */
  chiTiet?: (row: T) => ReactNode;
  /** Nhãn nút ▸ cho trình đọc màn hình, ví dụ `Chi tiết của Minh`. */
  nhanChiTiet?: (row: T) => string;
}

const CAN_LE = { left: 'text-left', right: 'text-right', center: 'text-center' } as const;

/**
 * Bảng dữ liệu theo thiết kế đã duyệt.
 *
 * Bốn thứ đổi so với bản cũ, mỗi thứ vì một lý do đo được:
 *
 * - **Header 12px/600 nền `header-bang`**, không viền dưới dày: hàng tiêu đề
 *   cũ cao bằng hàng dữ liệu nên mắt phải phân biệt bằng chữ, không bằng hình.
 * - **Hàng 9–10px/14px** thay cho 16px/20px: một trang 1440×900 trước đây chỉ
 *   thấy 8 dòng, nay thấy 14 — bảng này tồn tại để so sánh các dòng với nhau.
 * - **Ô rỗng in `—`**, không để trắng: trắng không phân biệt được "không có
 *   số" với "chưa tải xong".
 * - **Chân bảng đếm `8 / 12`** cạnh nút Tải thêm.
 */

export function DataTable<T>({
  columns,
  rows,
  keyOf,
  tongKet,
  dangTai = false,
  khiRong,
  conNua = false,
  dangTaiThem = false,
  onTaiThem,
  caption,
  tongSo,
  chiTiet,
  nhanChiTiet,
}: DataTableProps<T>) {
  const [moRong, setMoRong] = useState<Set<string>>(new Set());

  const doiMoRong = (khoa: string) => {
    setMoRong((cu) => {
      const moi = new Set(cu);
      if (moi.has(khoa)) moi.delete(khoa);
      else moi.add(khoa);
      return moi;
    });
  };
  if (!dangTai && rows.length === 0 && khiRong) {
    return <div className="rounded-the border border-vien bg-be-mat">{khiRong}</div>;
  }

  return (
    <div className="rounded-the border border-vien bg-be-mat">
      {/*
        Bảng rộng thì cuộn ngang trong khung riêng, không đẩy cả trang.

        `relative` là **bắt buộc**, không phải trang trí: `caption` và các ô
        tiêu đề `sr-only` là phần tử `position: absolute`. Không có khối chứa
        định vị ở đây, chúng neo vào gốc trang và nằm ở toạ độ x của cột tương
        ứng — tức ngoài khung nhìn trên điện thoại — nên `scrollWidth` của cả
        trang phình theo bề ngang bảng và trang cuộn ngang dù bảng đã có khung
        cuộn riêng (xem `e2e/91-khong-tran-ngang`).
      */}
      <div className="relative overflow-x-auto">
        <table className="w-full min-w-[640px] border-collapse text-bang">
          {caption && <caption className="sr-only">{caption}</caption>}
          <thead>
            <tr className="bg-header-bang">
              {chiTiet && <th scope="col" className="w-10 border-b border-vien" />}
              {columns.map((c) => (
                <th
                  key={c.key}
                  scope="col"
                  style={c.width ? { width: c.width } : undefined}
                  /*
                    `whitespace-nowrap`: tiêu đề cột **không xuống dòng**. Một
                    tiêu đề hai dòng làm hàng tiêu đề cao gấp đôi mọi hàng dữ
                    liệu, và cột hẹp lại (vì min-content của nó chỉ còn bằng từ
                    dài nhất) nên bảng trông chật hơn chứ không rộng ra. Bảng
                    quá rộng thì đã có khung cuộn ngang; tiêu đề dài thì rút
                    gọn và đưa bản đầy đủ vào `title`.
                  */
                  className={`border-b border-vien px-3.5 py-2.5 text-tieu-de-cot font-semibold whitespace-nowrap text-chu-phu ${CAN_LE[c.align ?? 'left']} ${c.ghim ? 'sticky left-0 z-10 bg-header-bang' : ''}`}
                >
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dangTai &&
              rows.length === 0 &&
              Array.from({ length: 3 }).map((_, i) => (
                <tr key={`khung-${String(i)}`}>
                  {chiTiet && <td className="border-b border-vien-trong" />}
                  {columns.map((c) => (
                    <td key={c.key} className="border-b border-vien-trong px-3.5 py-2.5">
                      <span className="block h-4 w-2/3 animate-pulse rounded bg-neutral-nen" />
                    </td>
                  ))}
                </tr>
              ))}

            {rows.map((row) => {
              const khoa = keyOf(row);
              const mo = moRong.has(khoa);

              return (
                <Fragment key={khoa}>
                  <tr className="transition-colors hover:bg-header-bang">
                    {chiTiet && (
                      <td className="border-b border-vien-trong px-1 py-2.5 align-top">
                        <button
                          type="button"
                          aria-expanded={mo}
                          aria-label={`${mo ? 'Thu gọn' : 'Mở'} ${nhanChiTiet?.(row) ?? 'chi tiết'}`}
                          onClick={() => {
                            doiMoRong(khoa);
                          }}
                          className="vung-bam-nho inline-flex w-9 items-center justify-center rounded-o text-chu-phu hover:bg-neutral-nen hover:text-chu"
                        >
                          {mo ? (
                            <ChevronDown size={16} aria-hidden="true" />
                          ) : (
                            <ChevronRight size={16} aria-hidden="true" />
                          )}
                        </button>
                      </td>
                    )}
                    {columns.map((c) => {
                      const noiDung = c.render(row);
                      return (
                        <td
                          key={c.key}
                          className={`border-b border-vien-trong px-3.5 py-2.5 text-chu ${CAN_LE[c.align ?? 'left']} ${c.ghim ? 'sticky left-0 z-10 bg-be-mat' : ''}`}
                        >
                          {/* `null`/`undefined`/chuỗi rỗng → `—`. `0` và `false`
                              vẫn in bình thường: 0 là một con số, không phải thiếu. */}
                          {noiDung === null || noiDung === undefined || noiDung === '' ? (
                            <span className="text-chu-phu">—</span>
                          ) : (
                            noiDung
                          )}
                        </td>
                      );
                    })}
                  </tr>

                  {chiTiet && mo && (
                    <tr className="bg-the-mo-rong">
                      <td
                        colSpan={columns.length + 1}
                        className="border-b border-vien-trong px-3.5 py-3"
                      >
                        {chiTiet(row)}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>

          {tongKet && (
            <tfoot>
              <tr className="bg-header-bang font-semibold">
                {chiTiet && <td />}
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={`px-3.5 py-2.5 text-chu ${CAN_LE[c.align ?? 'left']} ${c.ghim ? 'sticky left-0 z-10 bg-header-bang' : ''}`}
                  >
                    {tongKet[c.key] ?? null}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {(conNua || tongSo !== undefined) && (
        <div className="flex flex-wrap items-center gap-3 border-t border-vien px-3.5 py-2">
          <p className="so-lieu text-phu text-chu-phu">
            {tongSo === undefined
              ? `${String(rows.length)} dòng`
              : `${String(rows.length)} / ${String(tongSo)}`}
          </p>
          {conNua && (
            <span className="ml-auto">
              <Nut kieu="phu" dangChay={dangTaiThem} onClick={onTaiThem}>
                Tải thêm
              </Nut>
            </span>
          )}
        </div>
      )}
    </div>
  );
}
